import { ReportUserFilter } from "@/components/features/reports/ReportUserFilter";
import { ReportFilterBar } from "@/components/features/reports/ReportFilterBar";
import { useUserDirectory } from "@/hooks/use-user-directory";
import {
  Card,
  CardContent,
  DataTable,
  DataTableColumnHeader,
  DateFormField,
  Form,
  GlobalNotAuthorized,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { ReportPresetControls } from "@/components/features/reports/ReportPresetControls";
import { useReportFilterPresets } from "@/hooks/use-report-filter-presets";
import {
  getUsersOptions,
  retrieveMatterSettingsQueryOptions,
} from "@/lib/data/react-query";
import { retrieveReportsUserProductivityReportQueryOptions } from "@/lib/data/react-query/reports/reports-user-productivity-report";
import { REPORT_FILTER_PRESET_TYPES } from "@/lib/data/static/report-filter-preset-types";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type ReportsUserProductivityReportAggregate } from "@/lib/types";
import {
  normalizePresetStringArray,
  toReportPresetPreviewFilter,
} from "@/lib/utils/report-filter-presets";
import { MultiValueCell } from "@/lib/utils/tableUtils";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ColumnDef, type VisibilityState } from "@tanstack/react-table";
import {
  ClipboardList,
  Eye,
  Info,
  ListChecks,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import dayjs, { type Dayjs } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useForm } from "react-hook-form";
import { max } from "d3-array";
import { scaleLinear } from "d3-scale";
import { ReportsPageLayout } from "@/components/features/reports/ReportsPageLayout";

dayjs.extend(advancedFormat);

export const Route = createFileRoute("/_auth/reports/user-productivity")({
  component: RouteComponent,
  validateSearch: z.object({
    dateRangeType: z
      .enum([
        "ALL_TIME",
        "TODAY",
        "PREVIOUS_7_DAYS",
        "PREVIOUS_30_DAYS",
        "PREVIOUS_90_DAYS",
        "YEAR_TO_DATE",
        "CUSTOM_DATE_RANGE",
      ])
      .optional(),
    team: z.string().optional(),
    member: z.string().optional(),
    dateRangeStart: z.string().optional(),
    dateRangeEnd: z.string().optional(),
  }),
});

const DATE_RANGE_OPTIONS = [
  { value: "ALL_TIME", label: "All time" },
  { value: "TODAY", label: "Today" },
  { value: "PREVIOUS_7_DAYS", label: "Previous 7 days" },
  { value: "PREVIOUS_30_DAYS", label: "Previous 30 days" },
  { value: "PREVIOUS_90_DAYS", label: "Previous 90 days" },
  { value: "YEAR_TO_DATE", label: "Year to date" },
  { value: "CUSTOM_DATE_RANGE", label: "Custom range" },
] as const;

type DateRangeType = (typeof DATE_RANGE_OPTIONS)[number]["value"];
const DATE_RANGE_VALUES = new Set(DATE_RANGE_OPTIONS.map((item) => item.value));
const USER_PRODUCTIVITY_REPORT_TABLE_NAME =
  tanstackTableNames.reportsUserProductivity;
const USER_PRODUCTIVITY_REPORT_PRESET_COLUMN_IDS = {
  matterItemsCreated: "Matter Items Created",
  matterItemsDeleted: "Matter Items Deleted",
  matterItemsUpdated: "Matter Items Updated",
  mattersViewed: "Matters Viewed",
  team: "Team",
  user: "User",
} as const;
const USER_PRODUCTIVITY_REPORT_PRESET_COLUMN_KEYS = Object.fromEntries(
  Object.entries(USER_PRODUCTIVITY_REPORT_PRESET_COLUMN_IDS).map(
    ([key, value]) => [value, key],
  ),
) as Record<string, keyof typeof USER_PRODUCTIVITY_REPORT_PRESET_COLUMN_IDS>;
// Canonical action keys supplied by the backend for productivity aggregates.
const PRODUCTIVITY_ACTION_TYPES = [
  "MATTER_ASSIGNEE_CREATED",
  "MATTER_CONTACT_CONNECTION_CREATED",
  "MATTER_CREATED",
  "MATTER_EMAIL_CREATED",
  "MATTER_EXPENSE_CREATED",
  "MATTER_FILE_CREATED",
  "MATTER_FILE_REFERENCE_CREATED",
  "MATTER_INCIDENT_REPORT_CREATED",
  "MATTER_INSURANCE_CREATED",
  "MATTER_LIEN_CREATED",
  "MATTER_LITIGATION_DATE_CREATED",
  "MATTER_LOST_WAGES_CREATED",
  "MEDICAL_TREATMENT_APPOINTMENT_CREATED",
  "MEDICAL_TREATMENT_BALANCE_CONFIRMATION_CREATED",
  "MEDICAL_TREATMENT_BILL_CREATED",
  "MEDICAL_TREATMENT_CREATED",
  "MEDICAL_TREATMENT_PLAN_CREATED",
  "MEDICAL_TREATMENT_RECORD_CREATED",
  "MATTER_NOTE_CREATED",
  "MATTER_NOTE_COMMENT_CREATED",
  "MATTER_PROPERTY_DAMAGE_CREATED",
  "MATTER_RELATED_MATTER_CONNECTION_CREATED",
  "MATTER_SETTLEMENT_COUNTER_CREATED",
  "MATTER_SETTLEMENT_DEMAND_CREATED",
  "MATTER_SETTLEMENT_OFFER_CREATED",
  "MATTER_SHARE_FOLDER_CREATED",
  "MATTER_SHARE_FOLDER_FILE_CREATED",
  "MATTER_SIGNATURE_REQUEST_CREATED",
  "MATTER_STATUS_PHASE_CREATED",
  "MATTER_TAG_CONNECTION_CREATED",
  "MATTER_TASK_CREATED",
  "MATTER_TASK_COMMENT_CREATED",
  "MATTER_TEAM_CONNECTION_CREATED",
  "MATTER_TRUST_CHECK_CREATED",
  "MATTER_USER_CONNECTION_CREATED",
  "MATTER_VEHICLE_CREATED",
  "MATTER_ASSIGNEE_DELETED",
  "MATTER_CONTACT_CONNECTION_DELETED",
  "MATTER_DELETED",
  "MATTER_EMAIL_DELETED",
  "MATTER_EXPENSE_DELETED",
  "MATTER_FILE_DELETED",
  "MATTER_FILE_REFERENCE_DELETED",
  "MATTER_INCIDENT_REPORT_DELETED",
  "MATTER_INSURANCE_DELETED",
  "MATTER_LIEN_DELETED",
  "MATTER_LITIGATION_DATE_DELETED",
  "MATTER_LOST_WAGES_DELETED",
  "MEDICAL_TREATMENT_APPOINTMENT_DELETED",
  "MEDICAL_TREATMENT_BALANCE_CONFIRMATION_DELETED",
  "MEDICAL_TREATMENT_BILL_DELETED",
  "MEDICAL_TREATMENT_DELETED",
  "MEDICAL_TREATMENT_PLAN_DELETED",
  "MEDICAL_TREATMENT_RECORD_DELETED",
  "MATTER_NOTE_DELETED",
  "MATTER_NOTE_COMMENT_DELETED",
  "MATTER_PROPERTY_DAMAGE_DELETED",
  "MATTER_RELATED_MATTER_CONNECTION_DELETED",
  "MATTER_SETTLEMENT_COUNTER_DELETED",
  "MATTER_SETTLEMENT_DEMAND_DELETED",
  "MATTER_SETTLEMENT_OFFER_DELETED",
  "MATTER_SHARE_FOLDER_DELETED",
  "MATTER_SHARE_FOLDER_FILE_DELETED",
  "MATTER_SIGNATURE_REQUEST_DELETED",
  "MATTER_STATUS_PHASE_DELETED",
  "MATTER_TAG_CONNECTION_DELETED",
  "MATTER_TASK_DELETED",
  "MATTER_TASK_COMMENT_DELETED",
  "MATTER_TEAM_CONNECTION_DELETED",
  "MATTER_TRUST_CHECK_DELETED",
  "MATTER_USER_CONNECTION_DELETED",
  "MATTER_VEHICLE_DELETED",
  "MATTER_RETRIEVED",
  "MATTER_ASSIGNEE_UPDATED",
  "MATTER_CONTACT_CONNECTION_UPDATED",
  "MATTER_EXPENSE_UPDATED",
  "MATTER_FILE_REFERENCE_UPDATED",
  "MATTER_FILE_UPDATED",
  "MATTER_INCIDENT_REPORT_UPDATED",
  "MATTER_INSURANCE_UPDATED",
  "MATTER_LIEN_UPDATED",
  "MATTER_LITIGATION_DATE_UPDATED",
  "MATTER_LOST_WAGES_UPDATED",
  "MEDICAL_TREATMENT_APPOINTMENT_UPDATED",
  "MEDICAL_TREATMENT_BALANCE_CONFIRMATION_UPDATED",
  "MEDICAL_TREATMENT_BILL_UPDATED",
  "MEDICAL_TREATMENT_PLAN_UPDATED",
  "MEDICAL_TREATMENT_RECORD_UPDATED",
  "MEDICAL_TREATMENT_UPDATED",
  "MATTER_NOTE_UPDATED",
  "MATTER_NOTE_COMMENT_UPDATED",
  "MATTER_PROPERTY_DAMAGE_UPDATED",
  "MATTER_RELATED_MATTER_CONNECTION_UPDATED",
  "MATTER_SETTLEMENT_COUNTER_UPDATED",
  "MATTER_SETTLEMENT_DEMAND_UPDATED",
  "MATTER_SETTLEMENT_OFFER_UPDATED",
  "MATTER_SHARE_FOLDER_UPDATED",
  "MATTER_SHARE_FOLDER_FILE_UPDATED",
  "MATTER_SIGNATURE_REQUEST_UPDATED",
  "MATTER_STATUS_PHASE_UPDATED",
  "MATTER_TAG_CONNECTION_UPDATED",
  "MATTER_TASK_UPDATED",
  "MATTER_TASK_COMMENT_UPDATED",
  "MATTER_TEAM_CONNECTION_UPDATED",
  "MATTER_TRUST_CHECK_UPDATED",
  "MATTER_UPDATED",
  "MATTER_USER_CONNECTION_UPDATED",
  "MATTER_VEHICLE_UPDATED",
] as const;
const getActionTypeColumnId = (actionType: string) =>
  formatActionLabel(actionType);
const getActionTypePresetKey = (actionType: string) =>
  `actionType:${actionType}`;

type UserProductivityRow = ReportsUserProductivityReportAggregate & {
  actionCountsByType: Record<string, number>;
  userKey: string;
  userName: string;
  tasksCreated: number;
  tasksCompleted: number;
  outstandingTasks: number;
  itemsCreated: number;
  itemsUpdated: number;
  itemsDeleted: number;
  mattersViewed: number;
  totalActions: number;
  actionLabels: string[];
  sessionCount: number;
  firstActivity?: string;
  lastActivity?: string;
};

const parseSearchValues = (value?: string) =>
  value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const serializeSearchValues = (values: string[]) =>
  values.length ? values.join(",") : undefined;

const buildSearchFromPreset = (
  rawFilters: Record<string, unknown>,
): Record<string, string | undefined> => {
  const dateRangeType = DATE_RANGE_VALUES.has(
    rawFilters.dateRangeType as DateRangeType,
  )
    ? (rawFilters.dateRangeType as DateRangeType)
    : "ALL_TIME";

  return {
    dateRangeEnd:
      dateRangeType === "CUSTOM_DATE_RANGE" &&
      typeof rawFilters.dateRangeEnd === "string"
        ? rawFilters.dateRangeEnd
        : undefined,
    dateRangeStart:
      dateRangeType === "CUSTOM_DATE_RANGE" &&
      typeof rawFilters.dateRangeStart === "string"
        ? rawFilters.dateRangeStart
        : undefined,
    dateRangeType,
    member: serializeSearchValues(
      normalizePresetStringArray(rawFilters.member),
    ),
    team: serializeSearchValues(normalizePresetStringArray(rawFilters.team)),
  };
};

const numberFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const formatActionLabel = (value: string) =>
  value
    .split("_")
    .map((part) =>
      part ? `${part[0].toUpperCase()}${part.slice(1).toLowerCase()}` : "",
    )
    .filter(Boolean)
    .join(" ");

const extractActionEntries = (
  actions?: Array<Record<string, number>>,
): Array<{ action: string; count: number }> => {
  const totals = new Map<string, number>();
  if (!actions) return [];
  actions.forEach((item) => {
    Object.entries(item).forEach(([action, count]) => {
      if (typeof count !== "number" || Number.isNaN(count)) return;
      totals.set(action, (totals.get(action) ?? 0) + count);
    });
  });
  return Array.from(totals, ([action, count]) => ({ action, count }));
};

const sumByActionMatch = (
  entries: Array<{ action: string; count: number }>,
  match: RegExp,
) =>
  entries.reduce(
    (sum, item) => (match.test(item.action) ? sum + item.count : sum),
    0,
  );

const getUserKey = (report: ReportsUserProductivityReportAggregate) =>
  report.userProductivityReportAggregateUserId ??
  report.reportIndexPk ??
  report.userProductivityReportAggregateId ??
  report.pk ??
  report.sk ??
  "unknown";

const formatRangeLabel = (start?: string, end?: string) => {
  if (!start || !end) return undefined;
  const startDate = dayjs(start);
  const endDate = dayjs(end);
  if (!startDate.isValid() || !endDate.isValid()) {
    return undefined;
  }
  const currentYear = dayjs().year();
  const includeYear =
    startDate.year() !== currentYear || endDate.year() !== currentYear;
  const pattern = includeYear ? "MMM Do YYYY" : "MMM Do";
  const startLabel = startDate.format(pattern);
  if (startDate.isSame(endDate, "day")) {
    return startLabel;
  }
  const endLabel = endDate.format(pattern);
  return `${startLabel} - ${endLabel}`;
};

const ACTION_STACKS = [
  {
    key: "created",
    label: "Created",
    match: /_CREATED$/i,
    color: "var(--color-chart-3)",
  },
  {
    key: "updated",
    label: "Updated",
    match: /_UPDATED$/i,
    color: "var(--color-chart-2)",
  },
  {
    key: "deleted",
    label: "Deleted",
    match: /_DELETED$/i,
    color: "var(--color-chart-1)",
  },
  {
    key: "viewed",
    label: "Viewed",
    match: /_RETRIEVED$/i,
    color: "var(--color-chart-4)",
  },
  {
    key: "other",
    label: "Other",
    match: null,
    color: "var(--color-chart-5)",
  },
] as const;

type ActionStackKey = (typeof ACTION_STACKS)[number]["key"];

type ActionBucket = {
  start: Dayjs;
  end: Dayjs;
  label: string;
  totals: Record<ActionStackKey, number>;
  total: number;
};

type ActionBucketsResult = {
  buckets: ActionBucket[];
  totalActions: number;
  totalsByAction: Record<ActionStackKey, number>;
};

const buildYAxisTicks = (
  maxMinutes: number,
  {
    includeMid = false,
    formatter = (value: number) => numberFormatter.format(value),
  }: { includeMid?: boolean; formatter?: (value: number) => string } = {},
) => {
  if (maxMinutes <= 0) {
    return [{ label: formatter(0), position: 1 }];
  }
  const top = maxMinutes;
  const mid = maxMinutes / 2;
  const topLabel = formatter(top);
  const midLabel = formatter(mid);
  const bottomLabel = formatter(0);
  const ticks = [
    { label: topLabel, position: 0 },
    { label: bottomLabel, position: 1 },
  ];
  if (includeMid && midLabel !== topLabel && midLabel !== bottomLabel) {
    ticks.splice(1, 0, { label: midLabel, position: 0.5 });
  }
  return ticks;
};

const formatBucketLabel = (start: Dayjs, end: Dayjs, includeYear: boolean) => {
  const pattern = includeYear ? "MMM D YYYY" : "MMM D";
  if (start.isSame(end, "day")) {
    return start.format(pattern);
  }
  return `${start.format(pattern)} - ${end.format(pattern)}`;
};

const getTimelineRange = (
  timeline?: Array<{
    userProductivityReportDateTimeStart?: string;
    userProductivityReportDateTimeEnd?: string;
  }>,
) => {
  if (!timeline || timeline.length === 0) {
    return {
      sessionCount: 0,
      firstActivity: undefined,
      lastActivity: undefined,
    };
  }
  const timelineTimes = timeline
    .flatMap((entry) => [
      entry.userProductivityReportDateTimeStart,
      entry.userProductivityReportDateTimeEnd,
    ])
    .filter((value): value is string => Boolean(value))
    .filter((value) => dayjs(value).isValid());
  if (timelineTimes.length === 0) {
    return {
      sessionCount: timeline.length,
      firstActivity: undefined,
      lastActivity: undefined,
    };
  }
  const sortedTimes = [...timelineTimes].sort(
    (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf(),
  );
  return {
    sessionCount: timeline.length,
    firstActivity: sortedTimes[0],
    lastActivity: sortedTimes[sortedTimes.length - 1],
  };
};

const createActionTotals = (): Record<ActionStackKey, number> => ({
  created: 0,
  updated: 0,
  deleted: 0,
  viewed: 0,
  other: 0,
});

const buildActionTotals = (
  actions?: Array<Record<string, number>>,
): { totals: Record<ActionStackKey, number>; total: number } => {
  const entries = extractActionEntries(actions);
  const total = entries.reduce((sum, item) => sum + item.count, 0);
  const totals = createActionTotals();

  let knownSum = 0;
  ACTION_STACKS.forEach((stack) => {
    if (!stack.match) return;
    const value = sumByActionMatch(entries, stack.match);
    totals[stack.key] = value;
    knownSum += value;
  });

  totals.other = Math.max(total - knownSum, 0);
  return { totals, total };
};

const createActionBuckets = (
  rangeStart: Dayjs,
  rangeEnd: Dayjs,
  bucketSizeDays: number,
  includeYear: boolean,
) => {
  const buckets: ActionBucket[] = [];
  let cursor = rangeStart.startOf("day");
  const last = rangeEnd.endOf("day");

  while (cursor.isBefore(last) || cursor.isSame(last, "day")) {
    const bucketStart = cursor;
    const tentativeEnd = bucketStart
      .add(bucketSizeDays, "day")
      .subtract(1, "millisecond");
    const bucketEnd = tentativeEnd.isAfter(last) ? last : tentativeEnd;
    buckets.push({
      start: bucketStart,
      end: bucketEnd,
      label: formatBucketLabel(bucketStart, bucketEnd, includeYear),
      totals: createActionTotals(),
      total: 0,
    });
    cursor = bucketStart.add(bucketSizeDays, "day");
  }

  return buckets;
};

const buildActionBuckets = ({
  reports,
  rangeStart,
  rangeEnd,
  bucketSizeDays,
  includeYear,
}: {
  reports: ReportsUserProductivityReportAggregate[];
  rangeStart: Dayjs;
  rangeEnd: Dayjs;
  bucketSizeDays: number;
  includeYear: boolean;
}): ActionBucketsResult => {
  if (reports.length === 0) {
    return {
      buckets: createActionBuckets(
        rangeStart,
        rangeEnd,
        bucketSizeDays,
        includeYear,
      ),
      totalActions: 0,
      totalsByAction: createActionTotals(),
    };
  }

  const buckets = createActionBuckets(
    rangeStart,
    rangeEnd,
    bucketSizeDays,
    includeYear,
  );
  const totalsByAction = createActionTotals();
  let totalActions = 0;

  reports.forEach((report) => {
    const dateValue = report.userProductivityReportAggregateDate;
    if (!dateValue || !dayjs(dateValue).isValid()) {
      return;
    }
    const date = dayjs(dateValue).startOf("day");
    if (date.isBefore(rangeStart) || date.isAfter(rangeEnd)) {
      return;
    }
    const diffDays = date.diff(rangeStart, "day");
    const index = Math.floor(diffDays / bucketSizeDays);
    if (index < 0 || index >= buckets.length) {
      return;
    }
    const bucket = buckets[index];
    const { totals, total } = buildActionTotals(
      report.userProductivityReportAggregateActionsCompleted,
    );
    ACTION_STACKS.forEach((stack) => {
      const value = totals[stack.key];
      bucket.totals[stack.key] += value;
      totalsByAction[stack.key] += value;
    });
    bucket.total += total;
    totalActions += total;
  });

  return { buckets, totalActions, totalsByAction };
};

const ActionHistogram = ({
  buckets,
  height = 140,
  compact = false,
}: {
  buckets: ActionBucket[];
  height?: number;
  compact?: boolean;
}) => {
  if (buckets.length === 0) {
    return (
      <div className="text-muted-foreground text-sm">No activity recorded.</div>
    );
  }

  const maxValue = max(buckets, (bucket) => bucket.total) ?? 0;
  const yScale = scaleLinear()
    .domain([0, maxValue || 1])
    .range([0, height]);

  return (
    <div
      className={`grid w-full items-end ${compact ? "gap-0.5" : "gap-1"}`}
      style={{
        gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))`,
        height: `${height}px`,
      }}
      role="img"
      aria-label="Action histogram"
    >
      {buckets.map((bucket, index) => {
        const barHeight = Math.max(0, yScale(bucket.total));
        const segments = ACTION_STACKS.map((stack) => ({
          key: stack.key,
          label: stack.label,
          value: bucket.totals[stack.key],
          color: stack.color,
        })).filter((segment) => segment.value > 0);
        const tooltipLines = [
          bucket.label,
          ...(segments.length > 0
            ? segments.map(
                (segment) =>
                  `${segment.label}: ${numberFormatter.format(segment.value)}`,
              )
            : ["No data"]),
          `Total: ${numberFormatter.format(bucket.total)}`,
        ];
        const tooltipContent = tooltipLines.join("\n");
        return (
          <TooltipWrapper
            key={`${bucket.label}-${index}`}
            message={tooltipContent}
          >
            <div className="flex h-full items-end">
              <div
                className={`flex w-full flex-col-reverse ${
                  compact ? "rounded-sm" : "rounded-md"
                } overflow-hidden`}
                style={{ height: `${barHeight}px` }}
              >
                {segments.map((segment, segmentIndex) => {
                  const segmentHeight =
                    bucket.total > 0
                      ? (segment.value / bucket.total) * barHeight
                      : 0;
                  const isFirst = segmentIndex === 0;
                  const isLast = segmentIndex === segments.length - 1;
                  const radiusClass = compact
                    ? `${isFirst ? "rounded-b-sm" : ""} ${
                        isLast ? "rounded-t-sm" : ""
                      }`
                    : `${isFirst ? "rounded-b-md" : ""} ${
                        isLast ? "rounded-t-md" : ""
                      }`;
                  return (
                    <div
                      key={`${bucket.label}-${segment.key}-segment`}
                      className={radiusClass}
                      style={{
                        height: `${segmentHeight}px`,
                        backgroundColor: segment.color,
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </TooltipWrapper>
        );
      })}
    </div>
  );
};

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { hasPagePermission } = useUser();
  const search = Route.useSearch();

  const selectedDateRange: DateRangeType = search.dateRangeType ?? "ALL_TIME";
  const dateRangeStart = search.dateRangeStart;
  const dateRangeEnd = search.dateRangeEnd;
  const dateRangeForm = useForm<{
    dateRangeStart: string;
    dateRangeEnd: string;
  }>({
    defaultValues: {
      dateRangeStart: dateRangeStart ?? "",
      dateRangeEnd: dateRangeEnd ?? "",
    },
  });
  const watchedDateRangeStart = dateRangeForm.watch("dateRangeStart");
  const watchedDateRangeEnd = dateRangeForm.watch("dateRangeEnd");
  const [selectedTeams, setSelectedTeams] = useState<string[]>(() =>
    parseSearchValues(search.team),
  );
  const [selectedMembers, setSelectedMembers] = useState<string[]>(() =>
    parseSearchValues(search.member),
  );
  const [teamSelectValue, setTeamSelectValue] = useState<string | undefined>();

  const addUniqueValue = (values: string[], value: string) =>
    values.includes(value) ? values : [...values, value];

  useEffect(() => {
    setSelectedTeams(parseSearchValues(search.team));
  }, [search.team]);
  useEffect(() => {
    setSelectedMembers(parseSearchValues(search.member));
  }, [search.member]);
  useEffect(() => {
    if (selectedDateRange !== "CUSTOM_DATE_RANGE") {
      return;
    }
    if (dateRangeStart || dateRangeEnd) {
      return;
    }
    dateRangeForm.reset(
      { dateRangeStart: "", dateRangeEnd: "" },
      { keepDefaultValues: true },
    );
  }, [dateRangeEnd, dateRangeForm, dateRangeStart, selectedDateRange]);
  useEffect(() => {
    dateRangeForm.setValue("dateRangeStart", dateRangeStart ?? "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    dateRangeForm.setValue("dateRangeEnd", dateRangeEnd ?? "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [dateRangeEnd, dateRangeForm, dateRangeStart]);
  useEffect(() => {
    if (selectedDateRange !== "CUSTOM_DATE_RANGE") {
      return;
    }
    const nextStart = watchedDateRangeStart.trim();
    const nextEnd = watchedDateRangeEnd.trim();
    if (!nextStart || !nextEnd) {
      return;
    }
    if (nextStart === dateRangeStart && nextEnd === dateRangeEnd) {
      return;
    }
    void navigate({
      search: (prev) => ({
        ...prev,
        dateRangeStart: nextStart,
        dateRangeEnd: nextEnd,
      }),
      replace: true,
    });
  }, [
    dateRangeEnd,
    dateRangeStart,
    navigate,
    selectedDateRange,
    watchedDateRangeEnd,
    watchedDateRangeStart,
  ]);

  const canViewUserProductivityReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.firmReportsUserActivity,
  );

  const { data: matterSettingsData } = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    enabled: canViewUserProductivityReport,
  });
  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    ...getUsersOptions(),
    enabled: canViewUserProductivityReport,
  });

  const teamOptions = useMemo(() => {
    const teams = matterSettingsData?.data.teams ?? [];
    const uniqueLabels = Array.from(
      new Set(
        teams
          .map((team) => team.label.trim())
          .filter((label): label is string => Boolean(label)),
      ),
    ).sort((a, b) => a.localeCompare(b));

    const options = uniqueLabels.map((label) => ({ value: label, label }));
    const existing = new Set(options.map((option) => option.value));
    selectedTeams.forEach((value) => {
      if (!existing.has(value)) {
        options.push({ value, label: value });
      }
    });

    return options;
  }, [matterSettingsData, selectedTeams]);

  const userDirectory = useUserDirectory({
    enabled: canViewUserProductivityReport,
    selectedIds: selectedMembers,
  });

  const memberOptions = useMemo(() => {
    const users = userDirectory.users;
    const mappedUsers = users
      .map((user) => {
        const nameParts = [user.firstName.trim(), user.lastName.trim()].filter(
          Boolean,
        );
        const label = nameParts.join(" ");
        return {
          value: user.id,
          label: label || user.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    const options = [...mappedUsers];
    const existing = new Set(options.map((option) => option.value));
    selectedMembers.forEach((value) => {
      if (!existing.has(value)) {
        options.push({ value, label: value });
      }
    });

    return options;
  }, [selectedMembers, userDirectory.users]);

  const resolvedDateRange = useMemo(() => {
    if (selectedDateRange === "ALL_TIME") {
      return {
        end: undefined,
        start: undefined,
      };
    }

    if (selectedDateRange === "CUSTOM_DATE_RANGE") {
      return {
        end: dateRangeEnd,
        start: dateRangeStart,
      };
    }

    const today = dayjs().startOf("day");
    const start =
      selectedDateRange === "YEAR_TO_DATE"
        ? today.startOf("year")
        : selectedDateRange === "TODAY"
          ? today
          : today.subtract(
              selectedDateRange === "PREVIOUS_7_DAYS"
                ? 7
                : selectedDateRange === "PREVIOUS_30_DAYS"
                  ? 30
                  : 90,
              "day",
            );

    return {
      end: today.format("YYYY-MM-DD"),
      start: start.format("YYYY-MM-DD"),
    };
  }, [dateRangeEnd, dateRangeStart, selectedDateRange]);
  const hasCompleteDateRange = Boolean(
    selectedDateRange !== "ALL_TIME" &&
      resolvedDateRange.start &&
      resolvedDateRange.end,
  );
  const reportFilters = useMemo(
    () => ({
      dateRange: {
        preset: selectedDateRange,
        ...(selectedDateRange === "CUSTOM_DATE_RANGE" &&
        resolvedDateRange.start &&
        resolvedDateRange.end
          ? {
              endDate: resolvedDateRange.end,
              startDate: resolvedDateRange.start,
            }
          : {}),
      },
      filters: {
        teamIds: selectedTeams.map(
          (label) =>
            matterSettingsData?.data.teams.find((team) => team.label === label)
              ?.id ?? label,
        ),
        userIds: selectedMembers,
      },
    }),
    [
      matterSettingsData?.data.teams,
      resolvedDateRange.end,
      resolvedDateRange.start,
      selectedDateRange,
      selectedMembers,
      selectedTeams,
    ],
  );

  const { data, isLoading, error } = useQuery({
    ...retrieveReportsUserProductivityReportQueryOptions(reportFilters),
    enabled: canViewUserProductivityReport,
  });

  const reports = useMemo<ReportsUserProductivityReportAggregate[]>(
    () => data?.data ?? [],
    [data],
  );

  const dateRangeStartValue = useMemo(() => {
    if (!resolvedDateRange.start || !dayjs(resolvedDateRange.start).isValid()) {
      return undefined;
    }
    return dayjs(resolvedDateRange.start).startOf("day");
  }, [resolvedDateRange]);

  const dateRangeEndValue = useMemo(() => {
    if (!resolvedDateRange.end || !dayjs(resolvedDateRange.end).isValid()) {
      return undefined;
    }
    return dayjs(resolvedDateRange.end).endOf("day");
  }, [resolvedDateRange]);

  const filteredReports = useMemo(() => {
    if (!hasCompleteDateRange) {
      return reports;
    }
    return reports.filter((report) => {
      const date = report.userProductivityReportAggregateDate;
      if (!date || !dayjs(date).isValid()) {
        return false;
      }
      const reportDate = dayjs(date).startOf("day");
      if (dateRangeStartValue && reportDate.isBefore(dateRangeStartValue)) {
        return false;
      }
      if (dateRangeEndValue && reportDate.isAfter(dateRangeEndValue)) {
        return false;
      }
      return true;
    });
  }, [dateRangeEndValue, dateRangeStartValue, hasCompleteDateRange, reports]);

  const aggregatedReports = useMemo<
    ReportsUserProductivityReportAggregate[]
  >(() => {
    const aggregates = new Map<
      string,
      ReportsUserProductivityReportAggregate
    >();

    filteredReports.forEach((report) => {
      const userKey = getUserKey(report);
      const existing = aggregates.get(userKey);
      if (!existing) {
        aggregates.set(userKey, {
          ...report,
          userProductivityReportAggregateActionsCompleted: [
            ...(report.userProductivityReportAggregateActionsCompleted ?? []),
          ],
          userProductivityReportAggregateTimeline: [
            ...(report.userProductivityReportAggregateTimeline ?? []),
          ],
          userProductivityReportAggregateMatterTeam: [
            ...(report.userProductivityReportAggregateMatterTeam ?? []),
          ],
        });
        return;
      }

      const existingHours =
        typeof existing.userProductivityReportAggregateActivityTotalHours ===
          "number" &&
        !Number.isNaN(
          existing.userProductivityReportAggregateActivityTotalHours,
        )
          ? existing.userProductivityReportAggregateActivityTotalHours
          : 0;
      const nextHours =
        typeof report.userProductivityReportAggregateActivityTotalHours ===
          "number" &&
        !Number.isNaN(report.userProductivityReportAggregateActivityTotalHours)
          ? report.userProductivityReportAggregateActivityTotalHours
          : 0;

      const nextTeams = new Set(
        existing.userProductivityReportAggregateMatterTeam ?? [],
      );
      (report.userProductivityReportAggregateMatterTeam ?? []).forEach((team) =>
        nextTeams.add(team),
      );

      aggregates.set(userKey, {
        ...existing,
        userProductivityReportAggregateActivityTotalHours:
          existingHours + nextHours,
        userProductivityReportAggregateActionsCompleted: [
          ...(existing.userProductivityReportAggregateActionsCompleted ?? []),
          ...(report.userProductivityReportAggregateActionsCompleted ?? []),
        ],
        userProductivityReportAggregateTimeline: [
          ...(existing.userProductivityReportAggregateTimeline ?? []),
          ...(report.userProductivityReportAggregateTimeline ?? []),
        ],
        userProductivityReportAggregateMatterTeam: Array.from(nextTeams),
      });
    });

    return Array.from(aggregates.values());
  }, [filteredReports]);

  const users = useMemo(() => usersData?.data ?? [], [usersData]);
  const usersById = useMemo(
    () =>
      new Map(
        users.map((user) => [
          user.id,
          {
            firstName: user.firstName.trim(),
            lastName: user.lastName.trim(),
          },
        ]),
      ),
    [users],
  );

  const rows = useMemo<UserProductivityRow[]>(() => {
    return aggregatedReports.map((report) => {
      const userKey = getUserKey(report);
      const id = report.userProductivityReportAggregateUserId;
      const user = id ? usersById.get(id) : undefined;
      const nameParts = [user?.firstName ?? "", user?.lastName ?? ""].filter(
        Boolean,
      );
      const userName = nameParts.length > 0 ? nameParts.join(" ") : (id ?? "-");

      const actions = extractActionEntries(
        report.userProductivityReportAggregateActionsCompleted,
      ).sort((a, b) => b.count - a.count);
      const actionCountsByType = actions.reduce<Record<string, number>>(
        (acc, item) => {
          acc[item.action] = item.count;
          return acc;
        },
        {},
      );
      const totalActions = actions.reduce((sum, item) => sum + item.count, 0);
      const createdCount = sumByActionMatch(actions, /created/i);
      const completedCount = sumByActionMatch(actions, /completed/i);
      const tasksCreated = createdCount > 0 ? createdCount : totalActions;
      const tasksCompleted = completedCount > 0 ? completedCount : totalActions;
      const outstandingTasks = Math.max(tasksCreated - tasksCompleted, 0);
      const itemsCreated = sumByActionMatch(actions, /_CREATED$/i);
      const itemsUpdated = sumByActionMatch(actions, /_UPDATED$/i);
      const itemsDeleted = sumByActionMatch(actions, /_DELETED$/i);
      const mattersViewed = sumByActionMatch(actions, /_RETRIEVED$/i);
      const actionLabels = actions.map(
        (item) => `${formatActionLabel(item.action)} (${item.count})`,
      );

      const timelineRange = getTimelineRange(
        report.userProductivityReportAggregateTimeline,
      );

      return {
        ...report,
        actionCountsByType,
        userKey,
        userName,
        tasksCreated,
        tasksCompleted,
        outstandingTasks,
        itemsCreated,
        itemsUpdated,
        itemsDeleted,
        mattersViewed,
        totalActions,
        actionLabels,
        sessionCount: timelineRange.sessionCount,
        firstActivity: timelineRange.firstActivity,
        lastActivity: timelineRange.lastActivity,
      };
    });
  }, [aggregatedReports, usersById]);

  const defaultColumnVisibility = useMemo<VisibilityState>(
    () =>
      PRODUCTIVITY_ACTION_TYPES.reduce<VisibilityState>((acc, actionType) => {
        acc[getActionTypeColumnId(actionType)] = false;
        return acc;
      }, {}),
    [],
  );

  const resolveOptionLabel = (
    options: ReadonlyArray<{ value: string; label: string }>,
    value: string,
  ) => options.find((option) => option.value === value)?.label ?? value;

  const appliedFilterChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      onRemove: () => void;
    }> = [];

    if (hasCompleteDateRange) {
      const label =
        selectedDateRange === "CUSTOM_DATE_RANGE"
          ? (formatRangeLabel(resolvedDateRange.start, resolvedDateRange.end) ??
            "Custom range")
          : (DATE_RANGE_OPTIONS.find(
              (option) => option.value === selectedDateRange,
            )?.label ?? selectedDateRange);
      chips.push({
        key: `dateRange:${selectedDateRange}:${resolvedDateRange.start ?? ""}-${resolvedDateRange.end ?? ""}`,
        label: `Date range: ${label}`,
        onRemove: () => {
          void navigate({
            search: (prev) => ({
              ...prev,
              dateRangeType: "ALL_TIME",
              dateRangeStart: undefined,
              dateRangeEnd: undefined,
            }),
            replace: true,
          });
        },
      });
    }

    selectedTeams.forEach((value) => {
      chips.push({
        key: `team:${value}`,
        label: `Team: ${resolveOptionLabel(teamOptions, value)}`,
        onRemove: () => {
          const next = selectedTeams.filter((item) => item !== value);
          setSelectedTeams(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              team: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedMembers.forEach((value) => {
      chips.push({
        key: `member:${value}`,
        label: `Assignee: ${resolveOptionLabel(memberOptions, value)}`,
        onRemove: () => {
          const next = selectedMembers.filter((item) => item !== value);
          setSelectedMembers(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              member: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    return chips;
  }, [
    hasCompleteDateRange,
    memberOptions,
    navigate,
    resolvedDateRange.end,
    resolvedDateRange.start,
    selectedDateRange,
    selectedMembers,
    selectedTeams,
    teamOptions,
  ]);

  const tableDateRangeLabel = useMemo(() => {
    if (!hasCompleteDateRange) {
      return "All time";
    }
    if (selectedDateRange !== "CUSTOM_DATE_RANGE") {
      return (
        DATE_RANGE_OPTIONS.find((option) => option.value === selectedDateRange)
          ?.label ?? selectedDateRange
      );
    }
    return (
      formatRangeLabel(resolvedDateRange.start, resolvedDateRange.end) ??
      "Custom range"
    );
  }, [
    hasCompleteDateRange,
    resolvedDateRange.end,
    resolvedDateRange.start,
    selectedDateRange,
  ]);

  const summaryCounts = useMemo(() => {
    const uniqueUsers = new Set<string>();
    const uniqueDates = new Set<string>();
    let totalItemsCreated = 0;
    let totalItemsUpdated = 0;
    let totalItemsDeleted = 0;
    let totalMattersViewed = 0;

    rows.forEach((row) => {
      totalItemsCreated += row.itemsCreated;
      totalItemsUpdated += row.itemsUpdated;
      totalItemsDeleted += row.itemsDeleted;
      totalMattersViewed += row.mattersViewed;
      if (row.userProductivityReportAggregateUserId) {
        uniqueUsers.add(row.userProductivityReportAggregateUserId);
      }
      if (row.userProductivityReportAggregateDate) {
        uniqueDates.add(row.userProductivityReportAggregateDate);
      }
    });

    return {
      totalItemsCreated,
      totalItemsUpdated,
      totalItemsDeleted,
      totalMattersViewed,
      uniqueUsers: uniqueUsers.size,
      uniqueDates: uniqueDates.size,
    };
  }, [rows]);

  const summaryCards: Array<{
    label: string;
    value: number | string;
    meta?: string;
    icon: LucideIcon;
    showInfo?: boolean;
  }> = [
    {
      label: "Matter items created",
      value: numberFormatter.format(summaryCounts.totalItemsCreated),
      icon: ClipboardList,
    },
    {
      label: "Matter items updated",
      value: numberFormatter.format(summaryCounts.totalItemsUpdated),
      icon: ListChecks,
    },
    {
      label: "Matter items deleted",
      value: numberFormatter.format(summaryCounts.totalItemsDeleted),
      icon: Trash2,
    },
    {
      label: "Matters viewed",
      value: numberFormatter.format(summaryCounts.totalMattersViewed),
      icon: Eye,
    },
  ];

  const timelineRange = useMemo<{ start?: Dayjs; end?: Dayjs }>(() => {
    if (hasCompleteDateRange && dateRangeStartValue && dateRangeEndValue) {
      return {
        start: dateRangeStartValue,
        end: dateRangeEndValue,
      };
    }

    let earliest: Dayjs | undefined;
    let latest: Dayjs | undefined;
    filteredReports.forEach((report) => {
      const dateValue = report.userProductivityReportAggregateDate;
      if (!dateValue || !dayjs(dateValue).isValid()) {
        return;
      }
      const date = dayjs(dateValue);
      if (!earliest || date.isBefore(earliest)) {
        earliest = date;
      }
      if (!latest || date.isAfter(latest)) {
        latest = date;
      }
    });

    if (!earliest || !latest) {
      return {
        start: undefined,
        end: undefined,
      };
    }

    return {
      start: earliest.startOf("day"),
      end: latest.endOf("day"),
    };
  }, [
    dateRangeEndValue,
    dateRangeStartValue,
    filteredReports,
    hasCompleteDateRange,
  ]);

  const timelineBucketConfig = useMemo(() => {
    if (!timelineRange.start || !timelineRange.end) {
      return undefined;
    }
    const totalDays = timelineRange.end.diff(timelineRange.start, "day") + 1;
    const bucketSizeDays = totalDays > 45 ? 7 : 1;
    const includeYear =
      timelineRange.start.year() !== timelineRange.end.year() ||
      timelineRange.start.year() !== dayjs().year();

    return {
      totalDays,
      bucketSizeDays,
      includeYear,
    };
  }, [timelineRange]);

  const timelineRangeLabel = tableDateRangeLabel;

  const aggregateTimeline = useMemo(() => {
    if (!timelineRange.start || !timelineRange.end || !timelineBucketConfig) {
      return {
        buckets: [] as ActionBucket[],
        totalActions: 0,
        totalsByAction: createActionTotals(),
      };
    }

    return buildActionBuckets({
      reports: filteredReports,
      rangeStart: timelineRange.start,
      rangeEnd: timelineRange.end,
      bucketSizeDays: timelineBucketConfig.bucketSizeDays,
      includeYear: timelineBucketConfig.includeYear,
    });
  }, [filteredReports, timelineBucketConfig, timelineRange]);

  const aggregateMaxActions = useMemo(
    () => max(aggregateTimeline.buckets, (bucket) => bucket.total) ?? 0,
    [aggregateTimeline.buckets],
  );

  const timelineYAxisTicks = useMemo(() => {
    return buildYAxisTicks(aggregateMaxActions);
  }, [aggregateMaxActions]);

  const reportsByUserKey = useMemo(() => {
    const map = new Map<string, ReportsUserProductivityReportAggregate[]>();
    filteredReports.forEach((report) => {
      const key = getUserKey(report);
      const existing = map.get(key);
      if (existing) {
        existing.push(report);
      } else {
        map.set(key, [report]);
      }
    });
    return map;
  }, [filteredReports]);

  const timelineRows = useMemo(() => {
    const rangeStart = timelineRange.start;
    const rangeEnd = timelineRange.end;
    if (!rangeStart || !rangeEnd || !timelineBucketConfig) {
      return [];
    }

    return rows
      .map((row) => {
        const userReports = reportsByUserKey.get(row.userKey) ?? [];
        const result = buildActionBuckets({
          reports: userReports,
          rangeStart,
          rangeEnd,
          bucketSizeDays: timelineBucketConfig.bucketSizeDays,
          includeYear: timelineBucketConfig.includeYear,
        });
        return {
          row,
          ...result,
        };
      })
      .sort((a, b) => b.totalActions - a.totalActions);
  }, [reportsByUserKey, rows, timelineBucketConfig, timelineRange]);

  const timelineAxisTicks = useMemo(() => {
    if (!timelineRange.start || !timelineRange.end) {
      return [];
    }
    const pattern = timelineBucketConfig?.includeYear ? "MMM D YYYY" : "MMM D";
    const start = timelineRange.start;
    const end = timelineRange.end;
    if (start.isSame(end, "day")) {
      return [
        {
          label: start.format(pattern),
          position: 0,
        },
      ];
    }
    return [
      { label: start.format(pattern), position: 0 },
      { label: end.format(pattern), position: 1 },
    ];
  }, [
    timelineBucketConfig?.includeYear,
    timelineRange.end,
    timelineRange.start,
  ]);

  const timelineMiniAxisTicks = useMemo(() => {
    if (!timelineRange.start || !timelineRange.end) {
      return [];
    }
    const pattern = timelineBucketConfig?.includeYear ? "MMM D YY" : "MMM D";
    const startLabel = timelineRange.start.format(pattern);
    const endLabel = timelineRange.end.format(pattern);
    if (startLabel === endLabel) {
      return [{ label: startLabel, position: 0 }];
    }
    return [
      { label: startLabel, position: 0 },
      { label: endLabel, position: 1 },
    ];
  }, [
    timelineBucketConfig?.includeYear,
    timelineRange.end,
    timelineRange.start,
  ]);

  const reportColumns = useMemo<ColumnDef<UserProductivityRow>[]>(() => {
    const actionTypeColumns: ColumnDef<UserProductivityRow>[] =
      PRODUCTIVITY_ACTION_TYPES.map((actionType) => ({
        accessorFn: (row) => row.actionCountsByType[actionType] ?? 0,
        id: getActionTypeColumnId(actionType),
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) =>
          numberFormatter.format(
            row.original.actionCountsByType[actionType] ?? 0,
          ),
      }));
    const columns: ColumnDef<UserProductivityRow>[] = [
      {
        accessorKey: "userName",
        id: "User",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => row.original.userName || "-",
        enableHiding: false,
      },
      {
        accessorKey: "itemsCreated",
        id: "Matter Items Created",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => numberFormatter.format(row.original.itemsCreated),
      },
      {
        accessorKey: "itemsUpdated",
        id: "Matter Items Updated",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => numberFormatter.format(row.original.itemsUpdated),
      },
      {
        accessorKey: "itemsDeleted",
        id: "Matter Items Deleted",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => numberFormatter.format(row.original.itemsDeleted),
      },
      {
        accessorKey: "mattersViewed",
        id: "Matters Viewed",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => numberFormatter.format(row.original.mattersViewed),
      },
      ...actionTypeColumns,
      {
        accessorKey: "userProductivityReportAggregateMatterTeam",
        id: "Team",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <MultiValueCell
            value={row.original.userProductivityReportAggregateMatterTeam ?? []}
          />
        ),
      },
    ];

    return columns;
  }, []);

  const userProductivityPresetColumnKeysById = useMemo(
    () => ({
      ...USER_PRODUCTIVITY_REPORT_PRESET_COLUMN_KEYS,
      ...Object.fromEntries(
        PRODUCTIVITY_ACTION_TYPES.map((actionType) => [
          getActionTypeColumnId(actionType),
          getActionTypePresetKey(actionType),
        ]),
      ),
    }),
    [],
  );

  const {
    applyPresetByValue,
    clearSelectedPreset,
    createReportFilterPresetMutation,
    deletePresetByValue,
    deleteReportFilterPresetMutation,
    handleColumnOrderChange,
    handleColumnVisibilityChange,
    isReportFilterPresetsLoading,
    isSavePresetOpen,
    presetDescription,
    presetName,
    presetOptions,
    presetSelectValue,
    savePreset,
    savePreviewColumns,
    setIsSavePresetOpen,
    setPresetDescription,
    setPresetName,
    tableRenderKey,
  } = useReportFilterPresets({
    availableColumnIds: reportColumns
      .map((column) => column.id)
      .filter((columnId): columnId is string => typeof columnId === "string"),
    buildSearchFromPreset,
    currentFilterOptions: {
      dateRangeEnd:
        selectedDateRange === "CUSTOM_DATE_RANGE"
          ? resolvedDateRange.end
          : undefined,
      dateRangeStart:
        selectedDateRange === "CUSTOM_DATE_RANGE"
          ? resolvedDateRange.start
          : undefined,
      dateRangeType: selectedDateRange,
      member: selectedMembers,
      team: selectedTeams,
    },
    defaultColumnVisibility,
    enabled: canViewUserProductivityReport,
    lockedVisibleColumnIds: reportColumns
      .map((column) => column.id)
      .filter((columnId): columnId is string => columnId === "User"),
    onApplySearch: async (nextSearch) => {
      await navigate({
        search: () => nextSearch,
        replace: true,
      });
    },
    presetKeyByColumnId: userProductivityPresetColumnKeysById,
    reportType: REPORT_FILTER_PRESET_TYPES.userProductivity,
    tableName: USER_PRODUCTIVITY_REPORT_TABLE_NAME,
  });
  const getUserProductivityCsvValue = (
    row: UserProductivityRow,
    columnId: string,
  ) => {
    switch (columnId) {
      case "User":
        return row.userName || "-";
      case "Matter Items Created":
        return numberFormatter.format(row.itemsCreated);
      case "Matter Items Updated":
        return numberFormatter.format(row.itemsUpdated);
      case "Matter Items Deleted":
        return numberFormatter.format(row.itemsDeleted);
      case "Matters Viewed":
        return numberFormatter.format(row.mattersViewed);
      case "Team":
        return row.userProductivityReportAggregateMatterTeam?.length
          ? row.userProductivityReportAggregateMatterTeam.join(", ")
          : "-";
      default:
        return "";
    }
  };

  if (!canViewUserProductivityReport) {
    return <GlobalNotAuthorized />;
  }

  return (
    <ReportsPageLayout
      title="Productivity report"
      headerActions={
        <ReportPresetControls
          isDeleting={deleteReportFilterPresetMutation.isPending}
          isPresetListLoading={isReportFilterPresetsLoading}
          isSaveOpen={isSavePresetOpen}
          isSaving={createReportFilterPresetMutation.isPending}
          onDeletePresetValue={(value) => {
            void deletePresetByValue(value);
          }}
          onPresetDescriptionChange={setPresetDescription}
          onPresetNameChange={setPresetName}
          onPresetValueChange={(value) => {
            void applyPresetByValue(value);
          }}
          onSave={() => {
            void savePreset();
          }}
          onSaveOpenChange={setIsSavePresetOpen}
          presetDescription={presetDescription}
          presetName={presetName}
          presetOptions={presetOptions}
          presetValue={presetSelectValue}
          savePreviewColumns={savePreviewColumns}
          savePreviewFilters={appliedFilterChips.map((chip) =>
            toReportPresetPreviewFilter(chip.label),
          )}
          saveTitle="Save productivity report preset"
          selectId="user-productivity-report-preset-select"
        />
      }
    >
      <ReportFilterBar
        appliedFilters={appliedFilterChips.map((chip) => ({
          ...chip,
          onRemove: () => {
            clearSelectedPreset();
            chip.onRemove();
          },
        }))}
        onClearFilters={async () => {
          clearSelectedPreset();
          setSelectedTeams([]);
          setSelectedMembers([]);
          setTeamSelectValue(undefined);
          dateRangeForm.reset(
            { dateRangeStart: "", dateRangeEnd: "" },
            { keepDefaultValues: true },
          );
          await navigate({
            search: () => ({
              dateRangeType: "ALL_TIME",
              team: undefined,
              member: undefined,
              dateRangeStart: undefined,
              dateRangeEnd: undefined,
            }),
            replace: true,
          });
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            value={selectedDateRange}
            onValueChange={async (value) => {
              const nextValue = value as DateRangeType;
              await navigate({
                search: (prev) => {
                  if (nextValue === "ALL_TIME") {
                    return {
                      ...prev,
                      dateRangeType: "ALL_TIME",
                      dateRangeStart: undefined,
                      dateRangeEnd: undefined,
                    };
                  }

                  if (nextValue === "CUSTOM_DATE_RANGE") {
                    return {
                      ...prev,
                      dateRangeType: "CUSTOM_DATE_RANGE",
                      dateRangeStart: prev.dateRangeStart,
                      dateRangeEnd: prev.dateRangeEnd,
                    };
                  }

                  return {
                    ...prev,
                    dateRangeType: nextValue,
                    dateRangeStart: undefined,
                    dateRangeEnd: undefined,
                  };
                },
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select date range" />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGE_OPTIONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ReportUserFilter
            {...userDirectory.selectProps}
            items={memberOptions}
            onChange={async (value) => {
              const next = addUniqueValue(selectedMembers, value);
              setSelectedMembers(next);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  member: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          />
          <Select
            value={teamSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedTeams, value);
              setSelectedTeams(next);
              setTeamSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  team: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select teams" />
            </SelectTrigger>
            <SelectContent>
              {teamOptions.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedDateRange === "CUSTOM_DATE_RANGE" && (
            <Form {...dateRangeForm}>
              <div className="grid grid-cols-2 gap-3 sm:col-span-2 lg:col-span-2">
                <DateFormField
                  name="dateRangeStart"
                  label="Start date"
                  clearable={false}
                  register={dateRangeForm.register("dateRangeStart")}
                />
                <DateFormField
                  name="dateRangeEnd"
                  label="End date"
                  clearable={false}
                  register={dateRangeForm.register("dateRangeEnd")}
                />
              </div>
            </Form>
          )}
        </div>
      </ReportFilterBar>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.label} className="border-border/60 shadow-sm">
              <CardContent className="space-y-2 pt-4">
                {isLoading ? (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="bg-muted h-10 w-10 animate-pulse rounded-full" />
                      <div className="bg-muted h-4 w-24 animate-pulse rounded" />
                    </div>
                    <div className="bg-muted h-7 w-20 animate-pulse rounded" />
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="bg-muted text-foreground flex h-10 w-10 items-center justify-center rounded-full">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Text variant="subHeading">{card.label}</Text>
                        {card.showInfo ? (
                          <TooltipWrapper message="Total cumulative session time across all users.">
                            <Info className="text-muted-foreground h-4 w-4" />
                          </TooltipWrapper>
                        ) : null}
                      </div>
                    </div>
                    <Text variant="title" className="text-2xl leading-8">
                      {card.value}
                    </Text>
                    {card.meta ? (
                      <Text variant="subHeading" className="mt-0.5">
                        {card.meta}
                      </Text>
                    ) : null}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="w-fit">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4 space-y-6">
          <div className="space-y-4">
            <DataTable
              entityLabelPlural="users"
              key={tableRenderKey}
              hideSearchBar
              columns={reportColumns}
              data={rows}
              initialVisibility={defaultColumnVisibility}
              onColumnOrderChange={handleColumnOrderChange}
              onColumnVisibilityChange={handleColumnVisibilityChange}
              leftToolbarContent={
                <div className="space-y-1">
                  <Text variant="body" className="text-lg font-semibold">
                    Team productivity
                  </Text>
                  <Text variant="subHeading">{tableDateRangeLabel}</Text>
                </div>
              }
              onRowClick={() => {}}
              tableName={USER_PRODUCTIVITY_REPORT_TABLE_NAME}
              isLoading={isLoading || isUsersLoading}
              isError={Boolean(error)}
              noResultsMessage="No productivity data found."
              exportCsv={{
                fileName: "user-productivity-report.csv",
                getCellValue: getUserProductivityCsvValue,
              }}
            />
          </div>
        </TabsContent>
        <TabsContent value="timeline" className="mt-4 space-y-6">
          <div className="space-y-1">
            <Text variant="body" className="text-lg font-semibold">
              Team timeline
            </Text>
            <Text variant="subHeading">{timelineRangeLabel}</Text>
          </div>
          <Card className="border-border/60 shadow-sm">
            <CardContent className="space-y-4 pt-5">
              {isLoading ? (
                <div className="space-y-3">
                  <div className="bg-muted h-24 w-full animate-pulse rounded" />
                  <div className="bg-muted h-4 w-32 animate-pulse rounded" />
                </div>
              ) : aggregateTimeline.totalActions <= 0 ? (
                <Text variant="subHeading">
                  No activity timeline available for the selected filters.
                </Text>
              ) : (
                <>
                  <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-2 text-xs">
                    {ACTION_STACKS.map((stack) => (
                      <div key={stack.key} className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-sm"
                          style={{ backgroundColor: stack.color }}
                        />
                        <span>{stack.label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-4">
                    <div
                      className="text-muted-foreground relative w-12 shrink-0 text-[11px]"
                      style={{ height: "140px" }}
                    >
                      {timelineYAxisTicks.map((tick) => {
                        const alignment =
                          tick.position === 0
                            ? "translate-y-0"
                            : tick.position === 1
                              ? "-translate-y-full"
                              : "-translate-y-1/2";
                        return (
                          <div
                            key={`${tick.label}-${tick.position}`}
                            className={`absolute left-0 ${alignment}`}
                            style={{ top: `${tick.position * 100}%` }}
                          >
                            {tick.label}
                          </div>
                        );
                      })}
                    </div>
                    <div className="relative w-full px-2">
                      <div className="absolute inset-0">
                        {timelineYAxisTicks
                          .filter((tick) => tick.position !== 1)
                          .map((tick) => (
                            <div
                              key={`grid-${tick.label}-${tick.position}`}
                              className="border-border/40 absolute right-0 left-0 border-t"
                              style={{ top: `${tick.position * 100}%` }}
                            />
                          ))}
                      </div>
                      <div className="relative">
                        <ActionHistogram
                          buckets={aggregateTimeline.buckets}
                          height={140}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="relative mt-4 h-10 overflow-visible px-2">
                    <div className="border-border/60 absolute top-3 right-2 left-2 border-t" />
                    {timelineAxisTicks.map((tick) => {
                      const isStart = tick.position === 0;
                      const isEnd = tick.position === 1;
                      const alignment = isStart
                        ? "translate-x-0 text-left"
                        : isEnd
                          ? "-translate-x-full text-right"
                          : "-translate-x-1/2 text-center";
                      return (
                        <div
                          key={`${tick.label}-${tick.position}`}
                          className={`absolute top-0 ${alignment}`}
                          style={{
                            left: `${tick.position * 100}%`,
                            marginLeft: isStart ? "0.5rem" : undefined,
                            marginRight: isEnd ? "0.5rem" : undefined,
                          }}
                        >
                          <div className="bg-border/60 mx-auto h-2 w-px" />
                          <span className="text-muted-foreground text-[11px] leading-tight whitespace-nowrap">
                            {tick.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          <div className="space-y-1">
            <Text variant="body" className="text-lg font-semibold">
              Assignee activity
            </Text>
            <Text variant="subHeading">{timelineRangeLabel}</Text>
          </div>
          <Card className="border-border/60 shadow-sm">
            <CardContent className="space-y-4 pt-5 pb-6">
              {isLoading ? (
                <div className="space-y-3">
                  <div className="bg-muted h-16 w-full animate-pulse rounded" />
                  <div className="bg-muted h-16 w-full animate-pulse rounded" />
                </div>
              ) : timelineRows.length === 0 ? (
                <Text variant="subHeading">
                  No assignee activity available for the selected filters.
                </Text>
              ) : (
                <div className="grid gap-3">
                  {timelineRows.map((entry) => {
                    const rowMaxActions =
                      max(entry.buckets, (bucket) => bucket.total) ?? 0;
                    const rowYAxisTicks = buildYAxisTicks(rowMaxActions);
                    return (
                      <div
                        key={
                          entry.row.userProductivityReportAggregateUserId ??
                          entry.row.userName
                        }
                        className="border-border/60 rounded-lg border p-4"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <Text variant="body" className="font-semibold">
                              {entry.row.userName || "Unknown user"}
                            </Text>
                            <Text variant="caption">
                              {numberFormatter.format(entry.totalActions)}{" "}
                              actions
                            </Text>
                          </div>
                          {entry.row.userProductivityReportAggregateMatterTeam
                            ?.length ? (
                            <Text variant="caption">
                              {entry.row.userProductivityReportAggregateMatterTeam.join(
                                ", ",
                              )}
                            </Text>
                          ) : null}
                        </div>
                        {entry.totalActions <= 0 ? (
                          <Text variant="caption" className="mt-3">
                            No data
                          </Text>
                        ) : (
                          <div className="mt-3 flex gap-3">
                            <div
                              className="text-muted-foreground relative w-10 shrink-0 text-[10px]"
                              style={{ height: "48px" }}
                            >
                              {rowYAxisTicks.map((tick) => {
                                const alignment =
                                  tick.position === 0
                                    ? "translate-y-0"
                                    : tick.position === 1
                                      ? "-translate-y-full"
                                      : "-translate-y-1/2";
                                return (
                                  <div
                                    key={`${tick.label}-${tick.position}`}
                                    className={`absolute left-0 ${alignment}`}
                                    style={{
                                      top: `${tick.position * 100}%`,
                                    }}
                                  >
                                    {tick.label}
                                  </div>
                                );
                              })}
                            </div>
                            <div className="relative w-full px-2">
                              <div className="absolute inset-0">
                                {rowYAxisTicks
                                  .filter((tick) => tick.position !== 1)
                                  .map((tick) => (
                                    <div
                                      key={`row-grid-${tick.label}-${tick.position}`}
                                      className="border-border/30 absolute right-0 left-0 border-t"
                                      style={{
                                        top: `${tick.position * 100}%`,
                                      }}
                                    />
                                  ))}
                              </div>
                              <div className="relative">
                                <ActionHistogram
                                  buckets={entry.buckets}
                                  height={48}
                                  compact
                                />
                              </div>
                              {timelineMiniAxisTicks.length > 0 ? (
                                <div className="relative mt-3 h-6 overflow-visible px-2">
                                  <div className="border-border/40 absolute top-2 right-2 left-2 border-t" />
                                  {timelineMiniAxisTicks.map((tick) => {
                                    const isStart = tick.position === 0;
                                    const isEnd = tick.position === 1;
                                    const alignment = isStart
                                      ? "translate-x-0 text-left"
                                      : isEnd
                                        ? "-translate-x-full text-right"
                                        : "-translate-x-1/2 text-center";
                                    return (
                                      <div
                                        key={`row-axis-${tick.label}-${tick.position}`}
                                        className={`absolute top-0 text-[10px] ${alignment}`}
                                        style={{
                                          left: `${tick.position * 100}%`,
                                          marginLeft: isStart
                                            ? "0.5rem"
                                            : undefined,
                                          marginRight: isEnd
                                            ? "0.5rem"
                                            : undefined,
                                        }}
                                      >
                                        <div className="bg-border/40 mx-auto h-2 w-px" />
                                        <span className="text-muted-foreground leading-tight whitespace-nowrap">
                                          {tick.label}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </ReportsPageLayout>
  );
}
